/**
 * Real Client Store
 * State management using actual backend API data
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { apiService, SimpleClient, Portfolio } from '../services/api/apiService';

interface RealClientState {
  // Data
  clients: SimpleClient[];
  currentClient: SimpleClient | null;
  currentPortfolio: Portfolio | null;
  
  // Loading states
  isLoadingClients: boolean;
  isLoadingClient: boolean;
  isLoadingPortfolio: boolean;
  
  // Error states
  clientsError: string | null;
  clientError: string | null;
  portfolioError: string | null;
  
  // Actions
  loadClients: () => Promise<void>;
  loadClient: (clientId: string) => Promise<void>;
  loadClientPortfolio: (clientId: string) => Promise<void>;
  setCurrentClient: (client: SimpleClient | null) => void;
  clearErrors: () => void;
}

export const useRealClientStore = create<RealClientState>()(
  devtools(
    (set, _get) => ({
      // Initial state
      clients: [],
      currentClient: null,
      currentPortfolio: null,
      isLoadingClients: false,
      isLoadingClient: false,
      isLoadingPortfolio: false,
      clientsError: null,
      clientError: null,
      portfolioError: null,
      
      // Actions
      loadClients: async () => {
        set({ isLoadingClients: true, clientsError: null });
        
        try {
          const response = await apiService.getClients();
          set({ 
            clients: response.clients,
            isLoadingClients: false,
            clientsError: null
          });
        } catch (error) {
          set({ 
            isLoadingClients: false,
            clientsError: error instanceof Error ? error.message : 'Failed to load clients'
          });
        }
      },
      
      loadClient: async (clientId: string) => {
        set({ isLoadingClient: true, clientError: null });
        
        try {
          const client = await apiService.getClient(clientId);
          set({ 
            currentClient: client,
            isLoadingClient: false,
            clientError: null
          });
        } catch (error) {
          set({ 
            isLoadingClient: false,
            clientError: error instanceof Error ? error.message : 'Failed to load client'
          });
        }
      },
      
      loadClientPortfolio: async (clientId: string) => {
        set({ isLoadingPortfolio: true, portfolioError: null });
        
        try {
          const portfolio = await apiService.getClientPortfolio(clientId);
          set({ 
            currentPortfolio: portfolio,
            isLoadingPortfolio: false,
            portfolioError: null
          });
        } catch (error) {
          set({ 
            isLoadingPortfolio: false,
            portfolioError: error instanceof Error ? error.message : 'Failed to load portfolio'
          });
        }
      },
      
      setCurrentClient: (client: SimpleClient | null) => {
        set({ currentClient: client });
      },
      
      clearErrors: () => {
        set({ clientsError: null, clientError: null, portfolioError: null });
      }
    }),
    { name: 'real-client-store' }
  )
);

// Selectors
export const useClients = () => useRealClientStore(state => state.clients);
export const useCurrentRealClient = () => useRealClientStore(state => state.currentClient);
export const useCurrentPortfolio = () => useRealClientStore(state => state.currentPortfolio);
export const useClientLoadingStates = () => useRealClientStore(state => ({
  isLoadingClients: state.isLoadingClients,
  isLoadingClient: state.isLoadingClient,
  isLoadingPortfolio: state.isLoadingPortfolio
}));
export const useClientErrors = () => useRealClientStore(state => ({
  clientsError: state.clientsError,
  clientError: state.clientError,
  portfolioError: state.portfolioError
}));

// Actions
export const useRealClientActions = () => {
  const store = useRealClientStore();
  return {
    loadClients: store.loadClients,
    loadClient: store.loadClient,
    loadClientPortfolio: store.loadClientPortfolio,
    setCurrentClient: store.setCurrentClient,
    clearErrors: store.clearErrors
  };
};